// Site content hook — fetches editable content from /api/content with caching.
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Defaults mirror the backend seed so the UI never renders empty if the API is down.
export const DEFAULT_CONTENT = {
  hero: {
    est_line: "EST. 2023 / GUWAHATI, INDIA",
    headline: "Best Fried Friends Forever.",
    subtitle:
      "A fast-growing QSR brand serving freshly cooked crispy chicken, jumbo burgers, and signature flavored snacks — built in India, crafted by HomeChefs, made for the world.",
  },
  hero_stats: [
    { k: "6,000+", v: "Orders served" },
    { k: "75%", v: "Repeat-order rate" },
    { k: "4.3★", v: "Customer rating" },
    { k: "3 cities", v: "Free delivery zone" },
  ],
  cities: ["DULIAJAN", "DIBRUGARH", "GUWAHATI"],
  traction_stats: [
    { k: "6,000+", v: "Orders served", since: "since Mar 2024" },
    { k: "75%", v: "Repeat-order rate", since: "industry-leading" },
    { k: "₹16 L+", v: "Cumulative revenue", since: "bootstrapped" },
    { k: "4.3★", v: "Customer rating", since: "across aggregators" },
  ],
  deck_stats: [
    { k: "6,000+", v: "Orders served" },
    { k: "75%", v: "Repeat-order rate" },
    { k: "₹16 L+", v: "Cumulative revenue" },
    { k: "₹1.5 L+", v: "Avg revenue / outlet / month" },
    { k: "₹325", v: "Avg. order value" },
    { k: "4.3 ★", v: "Customer rating" },
    { k: "3", v: "HomeChefs onboarded" },
    { k: "3 cities", v: "Active in NE India" },
  ],
  deck_flavors: [
    { name: "TANGO", desc: "Sweet, tomato-tangy crunch — the crowd-pleaser." },
    { name: "PIPS", desc: "Familiar Indian palette, instant nostalgia." },
    { name: "XXX", desc: "Chatpata imli punch — sharp & addictive." },
    { name: "CHEESEX", desc: "Crispy crust, cheddar-cheese soul." },
    { name: "OREON", desc: "Onion-cream — unexpectedly delicious." },
  ],
  contact: {
    phone: "+91-6003700836",
    whatsapp: "+916003700836",
    whatsapp_message: "Hi Woodies! I'd like to order…",
    email: "team@woodiesfan.com",
    address: "Guwahati, India",
    swiggy_url:
      "https://www.swiggy.com/direct/brand/501492?source=swiggy-direct&subSource=google",
    zomato_url:
      "https://link.zomato.com/xqzv/rshare?id=7321149430563c68",
    instagram_url: "https://instagram.com/woodiessnacks",
    facebook_url: "https://www.facebook.com/profile.php?id=100090820382155",
    youtube_url: "",
    linkedin_url: "https://www.linkedin.com/company/woodiessnacks/",
    twitter_url: "https://twitter.com/Woodiesfans",
    legal_url: "/legal",
  },
  legal: {
    terms_updated: "27 JUNE 2025",
    privacy_updated: "27 JUNE 2025",
    terms_text: "",
    privacy_text: "",
  },
  email_templates: {
    investor_subject: "Re: Your Woodies inquiry",
    investor_body:
      "Hi {first_name},\n\nThanks for your interest in Woodies. I'd love to share our deck and walk you through the numbers — does a 30-minute call this week work?\n\nYou can grab a slot directly here: https://calendly.com/team-woodiesfan/30min\n\nLooking forward,\nTeam Woodies",
    homechef_subject: "Re: Your Woodies HomeChef interest",
    homechef_body:
      "Hi {first_name},\n\nThanks for raising your hand to cook with Woodies. We've reviewed your details and would love to schedule a quick chat to walk you through the program.\n\nWhat's a good time this week?\n\nWarm regards,\nTeam Woodies",
    franchisee_subject: "Re: Your Woodies franchise inquiry",
    franchisee_body:
      "Hi {first_name},\n\nExcited about your interest in bringing Woodies to a new city. Our franchise team will reach out shortly with the franchise pack and unit economics.\n\nIn the meantime, anything specific you'd like answered first?\n\nBest,\nTeam Woodies",
  },
};

let cache = null;
let inflight = null;
const CACHE_TTL_MS = 60 * 1000;
let cachedAt = 0;

export async function fetchSiteContent({ force = false } = {}) {
  const now = Date.now();
  if (!force && cache && now - cachedAt < CACHE_TTL_MS) return cache;
  if (inflight) return inflight;
  inflight = axios
    .get(`${API}/content`)
    .then(({ data }) => {
      cache = { ...DEFAULT_CONTENT, ...data };
      cachedAt = Date.now();
      return cache;
    })
    .catch(() => DEFAULT_CONTENT)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function invalidateSiteContent() {
  cache = null;
  cachedAt = 0;
}

export function useSiteContent() {
  const [content, setContent] = useState(cache || DEFAULT_CONTENT);
  useEffect(() => {
    let active = true;
    fetchSiteContent().then((c) => {
      if (active) setContent(c);
    });
    return () => {
      active = false;
    };
  }, []);
  return content;
}

// WhatsApp click-to-chat URL builder
export function buildWhatsAppUrl(contact) {
  const num = (contact?.whatsapp || "").replace(/[^0-9]/g, "");
  const msg = encodeURIComponent(contact?.whatsapp_message || "Hi Woodies!");
  return `https://wa.me/${num}?text=${msg}`;
}
